<script setup lang="ts">
/**
 * AGM 스튜디오 — ① 회원 구성 확인 게이트(Final Dividends 와 같은 방식: 회사 선택 → 판별 결과 확인/변경 → Proceed)
 *               ② 확인 후: 구성 배지(+다시 보기) · 생성될 문서들(옵션은 각 문서 안 조건 문단에서 토글)(제외 문서는 맨 아래에 사유와 함께).
 */
import { computed, ref } from 'vue';
import { useAgmStudio, type AgmMemberType } from './agmStudioContext';
import type { AgmStudioDocSlot } from './agmStudioConfig';

const props = defineProps<{ docs: AgmStudioDocSlot[] }>();

const { model, gate } = useAgmStudio();
const MEMBER_TYPES: AgmMemberType[] = ['multiple', 'sole_corporate', 'sole_individual'];
const LABEL: Record<AgmMemberType, string> = {
  multiple: 'Multiple Members (2+)',
  sole_corporate: 'Sole Corporate Member',
  sole_individual: 'Sole Individual Member',
};

const shown = computed(() => props.docs.filter((d) => d.included));
const skipped = computed(() => props.docs.filter((d) => !d.included));

const ZOOM_MIN = 100, ZOOM_MAX = 175, ZOOM_STEP = 25;
const docZoom = ref(100);
function setZoom(n: number) { docZoom.value = Math.min(ZOOM_MAX, Math.max(ZOOM_MIN, Math.round(n / ZOOM_STEP) * ZOOM_STEP)); }
</script>

<template>
  <div class="ap">
    <!-- ① 회원 구성 확인 게이트 -->
    <div v-if="!gate.confirmed.value" class="ag-gate">
      <div class="gate-icon">🔎</div>
      <div class="gate-title">Member Structure Summary</div>
      <p class="gate-lead">The AGM documents differ by member structure. Based on the records currently on file, this company's member structure is as follows.</p>
      <div v-if="gate.loading.value" class="gate-empty">Loading member records…</div>
      <div v-else-if="!gate.loaded.value" class="gate-empty">Select the company first — its member records will be checked here.</div>
      <template v-else>
        <div class="gate-result">
          <span class="gate-badge">{{ LABEL[model.memberType] }}</span>
          <span v-if="gate.summary.value.length" class="gate-count">{{ gate.summary.value.length }} member{{ gate.summary.value.length > 1 ? 's' : '' }}</span>
        </div>
        <p v-if="gate.detected.value && gate.detected.value !== model.memberType" class="gate-changed">Changed from the detected structure ({{ LABEL[gate.detected.value] }}).</p>
        <ul v-if="gate.summary.value.length" class="gate-members">
          <li v-for="(m, i) in gate.summary.value" :key="i">{{ m }}</li>
        </ul>
        <p v-else class="gate-empty">No member records on file. Choose the member structure below, or select the company again.</p>
      </template>
      <p class="gate-q">Proceed with this?</p>
      <div class="gate-actions">
        <button type="button" class="gate-proceed" @click="gate.confirmed.value = true">Proceed →</button>
        <label class="gate-override">
          If the member structure differs, change:
          <select v-model="model.memberType" class="scenario-sel">
            <option v-for="t in MEMBER_TYPES" :key="t" :value="t">{{ LABEL[t] }}</option>
          </select>
        </label>
      </div>
    </div>

    <!-- ② 문서 -->
    <template v-else>
      <div class="confirmed-head">
        <span class="ch-lbl">Member structure</span>
        <span class="ch-badge">{{ LABEL[model.memberType] }}</span>
        <button type="button" class="ch-reopen" @click="gate.confirmed.value = false">Review again</button>
      </div>
      <div class="zoom-bar">
        <div class="doc-zoom">
          <button type="button" class="doc-zoom-btn" :disabled="docZoom <= ZOOM_MIN" @click="setZoom(docZoom - ZOOM_STEP)">A−</button>
          <button type="button" class="doc-zoom-pct" @click="setZoom(100)">{{ docZoom }}%</button>
          <button type="button" class="doc-zoom-btn" :disabled="docZoom >= ZOOM_MAX" @click="setZoom(docZoom + ZOOM_STEP)">A+</button>
        </div>
      </div>
      <div class="paper-zoom" :style="{ zoom: docZoom / 100 }">
        <div v-for="(d, i) in shown" :key="d.key" class="pkg-doc">
          <div class="pkg-doclabel">
            <span class="ic">📝</span><span class="nm">{{ d.name }}</span>
            <span class="no">Doc {{ i + 1 }} / {{ shown.length }}</span>
          </div>
          <div class="simple-frame"><component :is="d.comp" v-bind="d.props || {}" /></div>
        </div>
        <div v-if="skipped.length" class="pkg-skip">
          <div class="pkg-skip-t">Not generated with the current options</div>
          <div v-for="d in skipped" :key="d.key" class="pkg-skip-r">· {{ d.name }} <span>— {{ d.reason }}</span></div>
        </div>
      </div>
    </template>
  </div>
</template>

<style scoped>
.ap { color: #111; }
/* 확인 게이트 (Final Dividends 와 같은 모양) */
.ag-gate { width: 560px; max-width: 100%; margin: 24px auto; padding: 28px 28px 24px; background: #fff; border: 1px solid #e6ebf3; border-radius: 16px; box-shadow: 0 8px 28px rgba(20,30,60,0.08); text-align: center; box-sizing: border-box; }
.gate-icon { font-size: 30px; }
.gate-title { font-size: 17px; font-weight: 800; color: #1f2d4d; margin: 8px 0 4px; }
.gate-lead { font-size: 13px; color: #5b6b82; margin: 0 0 16px; }
.gate-result { display: flex; align-items: center; justify-content: center; gap: 10px; margin: 0 0 12px; }
.gate-badge { background: #1b64da; color: #fff; font-weight: 800; font-size: 14px; padding: 7px 16px; border-radius: 999px; }
.gate-count { font-size: 12.5px; font-weight: 700; color: #42506a; }
.gate-changed { font-size: 11.5px; color: #b06a00; margin: -4px 0 12px; }
.gate-members { list-style: none; margin: 0 auto 14px; padding: 12px 16px; max-width: 420px; text-align: left; background: #f7f9fc; border: 1px solid #e6ebf3; border-radius: 10px; }
.gate-members li { font-size: 12.5px; color: #2c3a54; padding: 3px 0; border-bottom: 1px dashed #e2e8f2; }
.gate-members li:last-child { border-bottom: none; }
.gate-empty { font-size: 12.5px; color: #b06a00; background: #fff7e8; border: 1px solid #f0dcae; border-radius: 10px; padding: 10px 14px; margin: 0 0 14px; }
.gate-q { font-size: 14px; font-weight: 800; color: #1f2d4d; margin: 6px 0 16px; }
.gate-actions { display: flex; flex-direction: column; align-items: center; gap: 14px; }
.gate-proceed { border: none; background: #1b64da; color: #fff; font-size: 14px; font-weight: 800; border-radius: 10px; padding: 12px 28px; cursor: pointer; }
.gate-proceed:hover { background: #164fb0; }
.gate-override { font-size: 12px; color: #6b7a94; display: inline-flex; align-items: center; gap: 8px; }
.scenario-sel { border: 1px solid #b9d0ff; border-radius: 8px; padding: 6px 10px; font-size: 13px; font-weight: 700; color: #1f2d4d; background: #fff; }
/* 확인 후 헤더 */
.confirmed-head { display: flex; align-items: center; gap: 10px; width: 794px; max-width: 100%; margin: 0 auto 10px; padding: 8px 14px; background: #eafaf1; border: 1px solid #bfe6cf; border-radius: 10px; box-sizing: border-box; }
.ch-lbl { font-size: 11.5px; font-weight: 800; color: #42506a; }
.ch-badge { font-size: 12.5px; font-weight: 800; color: #14713f; background: #d6f2e1; padding: 3px 12px; border-radius: 999px; }
.ch-reopen { margin-left: auto; border: 1px solid #bfe6cf; background: #fff; color: #14713f; font-size: 11.5px; font-weight: 700; border-radius: 8px; padding: 3px 10px; cursor: pointer; }
/* 문서 */
.zoom-bar { display: flex; justify-content: flex-end; width: 794px; max-width: 100%; margin: 0 auto 12px; }
.doc-zoom { display: inline-flex; align-items: center; gap: 2px; background: #f2f4f6; border-radius: 10px; padding: 2px; }
.doc-zoom-btn, .doc-zoom-pct { border: none; background: transparent; cursor: pointer; font-weight: 800; color: #4b5563; border-radius: 8px; height: 32px; }
.doc-zoom-btn { min-width: 36px; font-size: 15px; }
.doc-zoom-btn:disabled { opacity: 0.35; cursor: default; }
.doc-zoom-pct { min-width: 52px; font-size: 12px; color: #3182f6; }
.paper-zoom { width: 794px; max-width: 100%; margin: 0 auto; }
.pkg-doc { margin-bottom: 26px; }
.pkg-doclabel { display: flex; align-items: center; gap: 8px; padding: 0 4px 10px; }
.pkg-doclabel .ic { font-size: 14px; }
.pkg-doclabel .nm { font-size: 12.5px; font-weight: 800; color: #42506a; }
.pkg-doclabel .no { margin-left: auto; font-size: 10.5px; font-weight: 800; color: #9aa6bd; white-space: nowrap; }
.simple-frame { width: 100%; background: #fff; border: 1px solid #e2e5ea; box-shadow: 0 4px 20px rgba(20,30,60,0.09); padding: 32px; box-sizing: border-box; overflow: hidden; font-size: 12px; }
.pkg-skip { border: 1px dashed #c9d3e4; border-radius: 10px; padding: 10px 14px; color: #5b6b82; font-size: 12px; margin-bottom: 20px; }
.pkg-skip-t { font-weight: 800; margin-bottom: 4px; }
.pkg-skip-r span { color: #9aa6bd; }
</style>
